import {
  ProviderAvatarBlock,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  DateFormat,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  Icon,
  ModalV2,
  Tag,
  TagColor,
  Text,
  getGroupClassIconElement,
  useToastMessage,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  ALL_CLASSES_REQUIRED_V2,
  REGISTRATION_CRITERIA,
  REGISTRATION_ENDS,
} from '~/lib/constants/calendar.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { Recurrence } from '~/lib/util/calendar.util';
import { useRegisterForGroupClassMutation } from '~/store/services/groupClass';

import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import { getProviderStateAtActionTime } from '~/lib/util/user.util';
import { DateTime } from 'luxon';
import { getRecurrenceDisplay } from '~/lib/util/schedule/schedule.util';
import {
  useGetTreatmentModalitiesEnumQuery,
  useGetTreatmentProgramsQuery,
} from '~/store/services/enumerations';
import { capitalizeFirstLetter } from '~/lib/common';
import { formatTimeRange, formatDateRange } from '~/lib/util/date.util';
import { formatAgeRangeDisplay } from '~/lib/util/clinicalProfile.util';
import { useMemo } from 'react';
import type { GroupClassKebabItem } from '~/lib/types/classes/GroupClassKebabItem';

const TOP_LEVEL_ID = 'group-class-details-modal';

interface GroupClassDetailsModalProps {
  groupClass: GroupClassExtended;
  handleKebabOption?: (id: string, option: string) => void;
  isOpen: boolean;
  kebabOptions?: GroupClassKebabItem[];
  onClose: () => void;
  onRegistration?: () => void;
  isRegisterEnabled?: boolean;
}

const GroupClassDetailsModal = ({
  groupClass,
  isOpen,
  kebabOptions,
  onClose,
  onRegistration,
  isRegisterEnabled,
}: GroupClassDetailsModalProps) => {
  const {
    ageRanges,
    attendeeRoles,
    description,
    diagnoses,
    duration,
    endDate,
    frequency,
    groupClassExternalId,
    hosts,
    isArchived,
    isHost,
    iconName,
    isSensitive,
    isUserRegistered,
    maximumAttendees,
    mustAttendAll,
    nextStartDate,
    registeredUsersCount,
    registrationEnds,
    showMaximumAttendees,
    startDate,
    title,
    treatmentModalities,
    treatmentPrograms,
  } = groupClass || {};

  const [registerForGroupClass] = useRegisterForGroupClassMutation();
  const { trackEvent } = useAnalytics();
  const { createToast } = useToastMessage();
  const {
    groupsHideProviderRegistration: isGroupsHideProviderRegistrationEnabled,
  } = useFlags<LDFlags>();

  const { data: treatmentModalitiesData } =
    useGetTreatmentModalitiesEnumQuery();

  const allModalities: string[] =
    groupClass?.treatmentModalities?.map((modality) => {
      const foundModality = treatmentModalitiesData?.find(
        (item) => item.code === modality,
      );
      return foundModality ? foundModality.label : modality;
    }) ?? [];

  const { data: allPrograms = [] } = useGetTreatmentProgramsQuery(undefined, {
    skip: !groupClass?.treatmentPrograms,
  });

  const matchedPrograms: string[] = useMemo(
    () =>
      treatmentPrograms?.length
        ? allPrograms
            .filter((p) => treatmentPrograms.includes(p.code))
            .map((p) => p.label)
        : [],
    [allPrograms, treatmentPrograms],
  );

  const effectiveIsRegisterEnabled = isRegisterEnabled ?? true;

  const onRegisterGroupClass = (): void => {
    registerForGroupClass({
      groupClassExternalId,
    })
      .unwrap()
      .then(() => {
        onRegistration?.();
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'group-class-modal-registration-failed',
          text: " We're sorry, we are unable to complete the action at this time. Please try again or reach out to support@equip.health",
          variant: 'critical',
        });
      });
  };
  const renderRegistrationTagOrButton = () => {
    if (effectiveIsRegisterEnabled && (isUserRegistered || isHost)) {
      return (
        <Tag
          color={isHost ? TagColor.periwinkle : TagColor.green}
          id={`${TOP_LEVEL_ID}__${isHost ? 'hosting' : 'registered'}`}
          size="md"
          value={isHost ? 'Hosting' : 'Registered'}
        />
      );
    }

    return (
      !isGroupsHideProviderRegistrationEnabled && (
        <FireflyButton
          id={`${TOP_LEVEL_ID}__register-btn`}
          onClick={() => {
            onRegisterGroupClass();
            trackEvent(
              GroupClassEvents.activeGroupsListDetailModalRegisterButtonClicked,
              {
                Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
              },
            );
          }}
        >
          Register
        </FireflyButton>
      )
    );
  };
  const renderRegistrationSection = () => {
    if (!isArchived && effectiveIsRegisterEnabled) {
      return renderRegistrationTagOrButton();
    }
    return null;
  };

  const nextStartDateFormatted = nextStartDate
    ? DateTime.fromISO(nextStartDate).toFormat('cccc, LLLL d')
    : '';

  const hasAnyCriteria =
    ageRanges ||
    attendeeRoles?.length <= 1 ||
    diagnoses?.length > 0 ||
    isSensitive ||
    mustAttendAll ||
    registrationEnds ||
    showMaximumAttendees ||
    treatmentPrograms?.length > 0 ||
    treatmentModalities?.length > 0;

  const renderTagList = (
    list: (string | number)[] | undefined,
    prefix: string,
    transform?: (item: string | number) => string,
  ) =>
    list?.map((item) => (
      <Tag
        color={TagColor.white}
        id={`${prefix}-${item}`}
        isBorderRequired
        key={`${prefix}-${item}`}
        value={transform ? transform(item) : String(item)}
      />
    )) || null;

  const showDoesNotRepeat = frequency === Recurrence.DOES_NOT_REPEAT;
  return (
    <ModalV2 id={TOP_LEVEL_ID} isOpen={isOpen} onClose={onClose} size="5xl">
      <ChakraBox
        flexDirection="column"
        justifyContent="center"
        minHeight="300px"
      >
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          padding="0px 30px"
        >
          {getGroupClassIconElement(iconName, '64px', '64px') ?? <Text />}
          <ChakraFlex gap="10px">
            {renderRegistrationSection()}
            <ChakraFlex justifyContent="center">
              {kebabOptions?.length > 0 && (
                <FireflyKebabMenu
                  aria-label="group class detail view kebab menu options"
                  options={kebabOptions}
                />
              )}
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
        <ChakraFlex justifyContent="space-between" padding="18px 30px 0px 30px">
          <ChakraFlex flexDirection="column" paddingRight="24px" width="586px">
            <Text color="black" id={`${TOP_LEVEL_ID}__title`} variant="h2">
              {title}
            </Text>
            <ChakraFlex flexWrap="wrap" width="100%">
              {(hosts ?? []).map((host: ScheduleUserBase) => {
                const credentials = host?.credentialsDetails?.map(
                  (credential) => credential.code,
                );
                const isDisplayName =
                  host.displayFirstName && host.displayLastName;
                const oooEndDate = getLatestOutOfOfficeEndDate(
                  host?.outOfOffice,
                );
                const providerTypeAtClassTime = getProviderStateAtActionTime(
                  host?.providerHistory,
                  startDate,
                );

                const providerType =
                  providerTypeAtClassTime?.providerTypeDetails?.name ||
                  host?.providerType;
                const deactivatedDate = DateTime.fromISO(
                  host?.deactivatedDate,
                ).startOf('day');
                const deactivatedDateFormatted = deactivatedDate?.isValid
                  ? deactivatedDate.toFormat(DateFormat.fullMonthDayYear)
                  : '';
                return (
                  <ChakraHStack
                    id={`${TOP_LEVEL_ID}__host-wrapper-${host.externalId}`}
                    key={`${TOP_LEVEL_ID}__host-wrapper-${host.externalId}`}
                    marginTop="12px"
                    width="50%"
                  >
                    <ProviderAvatarBlock
                      avatarUrl={
                        host?.avatar?.thumbnailUrl ?? host?.avatar?.url
                      }
                      credentials={credentials}
                      deactivatedDate={deactivatedDateFormatted}
                      displayName={
                        isDisplayName && {
                          firstName: host?.displayFirstName,
                          lastName: host?.displayLastName,
                        }
                      }
                      id={`${TOP_LEVEL_ID}__host-avatar-${host.externalId}`}
                      legalName={{
                        firstName: host.firstName,
                        lastName: host.lastName,
                      }}
                      oooDate={oooEndDate}
                      otherPronoun={host?.otherPronounText}
                      pronouns={host?.pronouns}
                      providerTypeName={providerType}
                      shouldShowCredentials
                      shouldShowDeactivatedDate
                      shouldShowOOOIcon={!!oooEndDate}
                      shouldShowPronouns
                      shouldShowProviderType
                      variant="standard"
                    />
                  </ChakraHStack>
                );
              })}
            </ChakraFlex>
            <Text
              color="black.70"
              id={`${TOP_LEVEL_ID}__description`}
              marginTop="24px"
              variant="body"
            >
              {description}
            </Text>
          </ChakraFlex>

          <ChakraFlex
            flexDirection="column"
            gap="20px"
            id={`${TOP_LEVEL_ID}__right-section-wrapper`}
            marginLeft="36px"
            width="278px"
          >
            <ChakraFlex flexDirection="column" gap="4px">
              <ChakraFlex alignItems="center" gap="4px">
                <ChakraBox id={`${TOP_LEVEL_ID}_icons`} width="24px">
                  <Icon
                    name={
                      showDoesNotRepeat
                        ? FAIconName.calendarDayRegular
                        : FAIconName.refreshRegular
                    }
                    size="md"
                  />
                </ChakraBox>

                <Text id={`${TOP_LEVEL_ID}__schedule-title`} variant="body">
                  {showDoesNotRepeat
                    ? (() => {
                        const d = new Date(startDate);
                        return ` ${d.toLocaleDateString(undefined, { weekday: 'long' })}, ${d.toLocaleDateString(undefined, { month: 'long' })} ${d.toLocaleDateString(undefined, { day: 'numeric' })}`;
                      })()
                    : getRecurrenceDisplay(
                        startDate,
                        frequency,
                        nextStartDate,
                        true,
                      )}
                </Text>
              </ChakraFlex>

              <ChakraFlex alignItems="center" gap="4px">
                <ChakraBox width="24px">
                  <Icon name={FAIconName.clockRegular} size="md" />
                </ChakraBox>

                <Text id={`${TOP_LEVEL_ID}_duration-text`} variant="body">
                  {formatTimeRange(nextStartDate ?? startDate, duration)}
                </Text>
              </ChakraFlex>

              {!showDoesNotRepeat && nextStartDateFormatted && (
                <ChakraFlex alignItems="center" gap="4px">
                  <ChakraBox width="24px">
                    <Icon name={FAIconName.forward} size="md" />
                  </ChakraBox>

                  <Text
                    id={`${TOP_LEVEL_ID}__next-start-date-text`}
                    variant="body"
                  >
                    Next on {nextStartDateFormatted}
                  </Text>
                </ChakraFlex>
              )}

              {Boolean(endDate) && (
                <ChakraFlex alignItems="center" gap="4px">
                  <ChakraBox width="24px">
                    <Icon name={FAIconName.arrowsLeftRight} size="md" />
                  </ChakraBox>

                  <Text id={`${TOP_LEVEL_ID}__start-date-text`} variant="body">
                    {formatDateRange(startDate, endDate)}
                  </Text>
                </ChakraFlex>
              )}

              <ChakraFlex alignItems="center" gap="4px">
                <ChakraBox id={`${TOP_LEVEL_ID}__group-icon`} width="24px">
                  <Icon name={FAIconName.userCheckRegular} size="md" />
                </ChakraBox>

                <ChakraFlex flexDirection="column">
                  <Text
                    id={`${TOP_LEVEL_ID}__total-registered-text`}
                    variant="body"
                  >
                    {registeredUsersCount} people registered
                  </Text>
                </ChakraFlex>
              </ChakraFlex>
            </ChakraFlex>
            {hasAnyCriteria && (
              <ChakraBox>
                <ChakraFlex align="center">
                  <ChakraBox width="24px">
                    <Icon
                      name={FAIconName.exclamationTriangleRegular}
                      size="sm"
                    />
                  </ChakraBox>

                  <Text variant="body">{REGISTRATION_CRITERIA}</Text>
                </ChakraFlex>

                <ChakraFlex
                  alignItems="center"
                  flexWrap="wrap"
                  gap="6px"
                  marginTop="6px"
                >
                  {(() => {
                    const roles = groupClass.attendeeRoles ?? [];
                    if (roles.length === 1) {
                      return renderTagList(roles, 'attendee-role', (x) =>
                        capitalizeFirstLetter(String(x).toLowerCase()),
                      );
                    }
                    return null;
                  })()}
                  {renderTagList(matchedPrograms, 'treatment-programs')}
                  {renderTagList(allModalities, 'modality')}
                  {renderTagList(groupClass.diagnoses, 'diagnoses')}
                  {Array.isArray(ageRanges)
                    ? renderTagList(
                        ageRanges,
                        'age-range',
                        formatAgeRangeDisplay,
                      )
                    : ageRanges &&
                      renderTagList(
                        [ageRanges],
                        'age-range',
                        formatAgeRangeDisplay,
                      )}
                  {showMaximumAttendees &&
                    renderTagList(
                      [`${maximumAttendees} max attendees`],
                      'maximum-attendees',
                    )}
                  {registrationEnds &&
                    renderTagList([REGISTRATION_ENDS], 'registration-ends')}
                  {mustAttendAll &&
                    renderTagList(
                      [ALL_CLASSES_REQUIRED_V2],
                      'all-classes-required',
                    )}
                  {isSensitive &&
                    renderTagList(
                      ['Marked as sensitive'],
                      'marked-as-sensitive',
                    )}
                </ChakraFlex>
              </ChakraBox>
            )}
          </ChakraFlex>
        </ChakraFlex>
      </ChakraBox>
    </ModalV2>
  );
};

export default GroupClassDetailsModal;
